import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { Extent } from "ol/extent";
import { transformExtent } from "ol/proj";
import { useFormApi } from "@avinet/adaptive-ui-core/form/useForm";
import FormError from "@avinet/adaptive-ui-core/form/utils/FormError";
import FormInfo from "@avinet/adaptive-ui-core/form/utils/FormInfo";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import FormBaseProps from "../../../types/FormBaseProps";
import { GeometrySectionType } from "../../../types/Section";
import { Icon } from "../../Icon";
import DefaultMap from "../../map/DefaultMap";
import LineGeometryPicker from "../../map/LineGeometryPicker";
import PointGeometryPicker from "../../map/PointGeometryPicker";
import PolygonGeometryPicker from "../../map/PolygonGeometryPicker";
import "./GeometryControl.scss";

interface GeometryControlProps extends FormBaseProps {
  /**
   * The input field type.
   *
   * Only a subset of HTML `input` types are supported. Use other controls for other types.
   */
  type?: "geometry_point" | "geometry_line" | "geometry_polygon";
  /**
   * Is the field required.
   */
  required?: boolean;
  /**
   * Help text to display for the map.
   *
   * If set to true, an informative text is appended, translated by the the key `${id}Info` or `${langKey}Info`.
   */
  info?: boolean | string;
  /**
   * Base map uuid.
   */
  basemap?: string;
  /**
   * Initial extent in EPSG:4326.
   */
  extent?: Extent;
}

function GeometryControl({
  id,
  className,
  langKey,
  label,
  info = false,
  type,
  required = false,
  basemap,
  extent,
}: GeometryControlProps): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const t = useTranslate("Form");
  const form = useFormApi();
  const { getValue, setValue } = form;
  const [rawValue, setRawValue] = useState(
    () => getValue(id)?.toString() ?? ""
  );
  const [isValid, setValid] = useState<boolean>(true);
  const [showInfo, setShowInfo] = useState(false);

  if (!langKey && typeof id === "string") langKey = id;

  const layers = useMemo(() => {
    if (!basemap) return undefined;
    return [
      {
        id: "basemap",
        sourceType: "adaptive",
        themeType: "basetheme",
        themeUuid: basemap,
      },
    ];
  }, [basemap]);

  useEffect(() => {
    const value = rawValue;
    if (getValue(id) === value) return;
    setValue(id, value);
  }, [id, type, rawValue, getValue, setValue]);

  const transformedExtent = useMemo(() => {
    if (!extent) return;

    return transformExtent(extent, "EPSG:4326", "EPSG:3857");
  }, [extent]);

  const picker = (() => {
    switch (type) {
      case GeometrySectionType.geometry_point:
        return (
          <PointGeometryPicker
            value={rawValue}
            onValueChange={setRawValue}
            onValidityChange={setValid}
            autoZoom
          />
        );

      case GeometrySectionType.geometry_line:
        return (
          <LineGeometryPicker
            value={rawValue}
            onValueChange={setRawValue}
            onValidityChange={setValid}
            autoZoom
          />
        );

      case GeometrySectionType.geometry_polygon:
        return (
          <PolygonGeometryPicker
            value={rawValue}
            onValueChange={setRawValue}
            onValidityChange={setValid}
            autoZoom
          />
        );
    }
  })();

  return (
    <div ref={ref} className={`geometry-control ${className ?? ""}`}>
      {label !== null && (
        <label htmlFor={id}>
          {label || t(langKey + "Label")}{" "}
          {!required && <span className="optional">({t("optional")})</span>}
        </label>
      )}
      <DefaultMap layers={layers} initialExtent={transformedExtent}>
        {picker}
        {!!info && (
          <div className="help-container">
            <button
              type="button"
              className="btn map icon help"
              onClick={() => setShowInfo((show) => !show)}
              title="Help"
            >
              <Icon name="help" />
            </button>
            {showInfo && <FormInfo info={info} langKey={langKey} t={t} />}
          </div>
        )}
      </DefaultMap>
      <input
        aria-hidden="true"
        aria-disabled="true"
        className="force-required"
        type="text"
        value={!!rawValue ? (isValid ? "true" : "false") : ""}
        required={required}
        pattern="^(true)?$"
        onChange={(e) => (e.target.value = isValid ? "true" : "")}
        tabIndex={-1}
        title={
          isValid
            ? ""
            : type === "geometry_polygon"
            ? t("polygonInvalid")
            : t("lineInvalid")
        }
      />
      <FormError id={id} />
    </div>
  );
}

export default GeometryControl;
